import { useCallback, useMemo } from 'react';
import {
  openApp as fireOpenApp,
  openInstall as fireOpenInstall,
  type LaunchOutcome,
} from '../lib/deeplink';
import { detectPlatform, type Platform } from '../lib/platform';

interface Result {
  platform: Platform;
  openApp: () => LaunchOutcome;
  openInstall: () => LaunchOutcome;
}

/**
 * dev 환경에서만 ?_ua=<string> 으로 navigator.userAgent 를 강제 오버라이드.
 * 카카오톡 / iOS Safari / Chrome Android 등 기기 없이 시뮬레이션 가능.
 */
function resolveUaOverride(): string | null {
  if (typeof window === 'undefined') return null;
  if (!import.meta.env.DEV) return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('_ua');
  return raw && raw.length > 0 ? raw : null;
}

/**
 * Universal Link self-URL 전략: 현재 페이지 URL 자체가 deep link target 이므로
 * 별도의 route/trackingId argument 가 필요 없다 (`buildDeepLinkTarget` 내부에서 처리).
 * 따라서 이 훅은 인자 없이도 동작한다.
 */
export function useDeeplinkLauncher(): Result {
  const platform = useMemo(() => detectPlatform(resolveUaOverride()), []);

  const openApp = useCallback(() => fireOpenApp(platform), [platform]);
  const openInstall = useCallback(() => fireOpenInstall(platform), [platform]);

  return { platform, openApp, openInstall };
}
